/**
 * Central Product Master — enums & display labels
 * Values must match dashboard-backend MODELS_CONSTANTS / PRODUCT_MASTER_CONSTANTS.
 */

export const CPM_QUEUE_TYPE = {
    PENDING_PROMOTION: 'PENDING_PROMOTION',
    LINK_REVIEW: 'LINK_REVIEW',
    BARCODE_CONFLICT: 'BARCODE_CONFLICT',
} as const;

export const CPM_QUEUE_TAB = {
    pending: 'pending',
    link: 'link',
    barcode: 'barcode',
} as const;

export const CPM_TAB_TO_QUEUE_TYPE = {
    pending: CPM_QUEUE_TYPE.PENDING_PROMOTION,
    link: CPM_QUEUE_TYPE.LINK_REVIEW,
    barcode: CPM_QUEUE_TYPE.BARCODE_CONFLICT,
} as const;

export const CPM_RECORD_TYPE = {
    BRAND: 'BRAND',
    PRODUCT: 'PRODUCT',
    VARIANT: 'VARIANT',
} as const;

export const CPM_LEVEL = {
    brand: 'brand',
    product: 'product',
    variant: 'variant',
    stateProduct: 'stateProduct',
} as const;

export const CPM_CONFIDENCE_BAND = {
    VERY_HIGH: 'VERY_HIGH',
    HIGH: 'HIGH',
    MODERATE: 'MODERATE',
    LOW: 'LOW',
} as const;

export const CPM_LINK_METHOD = {
    BARCODE: 'BARCODE',
    DETERMINISTIC: 'DETERMINISTIC',
    FUZZY: 'FUZZY',
    LLM: 'LLM',
    HUMAN: 'HUMAN',
} as const;

export const CPM_PRODUCT_LINK_MASTER_TYPE = {
    ALCOHOL_BRAND: 'ALCOHOL_BRAND',
    ALCOHOL_PRODUCT: 'ALCOHOL_PRODUCT',
    PRODUCT_VARIANT: 'PRODUCT_VARIANT',
} as const;

/**
 * Match-trace signal rows for Link Review modal.
 * Weights mirror dashboard-backend PRODUCT_MASTER_CONSTANTS.COMPOSITE_CONFIDENCE.WEIGHTS
 * — keep in sync when backend retunes scoring.
 */
export const CPM_MATCH_SIGNAL_ROWS = [
    {
        key: 'lexical',
        label: 'Text similarity',
        code: 'LEXICAL',
        plain: 'How closely the tenant name reads like the candidate name.',
        weight: 0.3,
    },
    {
        key: 'brandToken',
        label: 'Brand token match',
        code: 'BRAND_TOKEN',
        plain: 'Whether the candidate brand name appears in the tenant record.',
        weight: 0.25,
    },
    {
        key: 'volumeExact',
        label: 'Exact volume match',
        code: 'VOLUME_EXACT',
        plain: 'Whether pack size matches exactly (ml).',
        weight: 0.2,
    },
    {
        key: 'learnedAlias',
        label: 'Learned alias',
        code: 'LEARNED_ALIAS',
        plain: 'Whether this master was confirmed in a prior human link.',
        weight: 0.1,
    },
    {
        key: 'scoreGap',
        label: 'Lead over next candidate',
        code: 'SCORE_GAP',
        plain: 'How far this candidate led the next-best option.',
        weight: 0.1,
    },
    {
        key: 'containerMatch',
        label: 'Container match',
        code: 'CONTAINER_MATCH',
        plain: 'Whether glass / PET / can wording agrees.',
        weight: 0.05,
    },
    {
        key: 'itemCodeBoost',
        label: 'State item-code boost',
        code: 'ITEM_CODE_BOOST',
        plain: 'Maharashtra item-code agreement, when available.',
        weight: 0.1,
    },
] as const;

export const CPM_QUEUE_SIGNAL = {
    DUP: 'DUP',
    NONE: 'NONE',
} as const;

export const CPM_ALIAS_SCOPE = {
    STORE: 'STORE',
    GLOBAL: 'GLOBAL',
    BOTH: 'BOTH',
    NONE: 'NONE',
} as const;

export const CPM_QUEUE_RESOLUTION = {
    ACCEPT_AI: 'ACCEPT_AI',
    PICK_DIFFERENT: 'PICK_DIFFERENT',
    CREATE_NEW: 'CREATE_NEW',
} as const;

export const CPM_REJECT_REASON = {
    HALLUCINATED_OR_GARBLED_NAME: 'HALLUCINATED_OR_GARBLED_NAME',
    NOT_ALCOHOLIC: 'NOT_ALCOHOLIC',
    TEST_OR_JUNK_DATA: 'TEST_OR_JUNK_DATA',
    OTHER: 'OTHER',
} as const;

export const CPM_REJECT_REASON_OPTIONS = [
    {
        value: CPM_REJECT_REASON.HALLUCINATED_OR_GARBLED_NAME,
        label: 'Hallucinated or garbled name',
    },
    {
        value: CPM_REJECT_REASON.NOT_ALCOHOLIC,
        label: 'Not alcoholic',
    },
    {
        value: CPM_REJECT_REASON.TEST_OR_JUNK_DATA,
        label: 'Test or junk data',
    },
    {
        value: CPM_REJECT_REASON.OTHER,
        label: 'Other',
    },
] as const;

export const CPM_TENANT_MERGE_ACTION = {
    REPOINT: 'REPOINT',
    UNLINK: 'UNLINK',
} as const;

/** Live catalog statuses — same set as dashboard-backend LIVE_MASTER_STATUSES. */
export const CPM_LIVE_MASTER_STATUSES = ['PENDING', 'ACTIVE', 'NEEDS_REVIEW'] as const;

export const CPM_BARCODE_RESOLUTION = {
    ASSIGN_TO: 'ASSIGN_TO',
    RETIRE: 'RETIRE',
    ACKNOWLEDGE: 'ACKNOWLEDGE',
} as const;

/** Default Verification Queue page size — matches Master Editor Rows control. */
export const CPM_QUEUE_PAGE_SIZE = 25;

/** Collapse Pending chain-card children when a family exceeds this many nodes. */
export const CPM_PENDING_CHAIN_COLLAPSE_AFTER = 12;

export const CPM_MASTER_PAGE_SIZE = 25;

export const CPM_MASTER_AREA = {
    brands: 'brands',
    catalog: 'catalog',
    stateProducts: 'stateProducts',
} as const;

export const CPM_CATALOG_VIEW = {
    combined: 'combined',
    products: 'products',
    variants: 'variants',
} as const;

export const CPM_CONTAINER_TYPE_OPTIONS = [
    { value: 'Can', label: 'Can' },
    { value: 'Glass Bottle', label: 'Glass Bottle' },
    { value: 'Keg', label: 'Keg' },
    { value: 'Pet Bottle', label: 'Pet Bottle' },
    { value: 'Pint', label: 'Pint' },
    { value: 'Tetra Pack', label: 'Tetra Pack' },
] as const;

export const CPM_MASTER_STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'NEEDS_REVIEW', label: 'Needs review' },
    { value: 'MERGED', label: 'Merged' },
] as const;

export const CPM_INDIAN_STATES = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Delhi',
    'Puducherry',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Andaman and Nicobar Islands',
] as const;

export const CPM_VOLUME_UNIT_SHORT: Record<string, string> = {
    'ML Millilitre': 'ML',
    'L Litre': 'L',
    'MLG Milligram': 'MLG',
    'GMS Grams': 'GMS',
    'KGS Kilograms': 'KGS',
    'UNT Units': 'UNT',
    'OTH Other': 'OTH',
};

/** Units offered on Volume Settings add/edit (design: ML / L only). */
export const CPM_VOLUME_UNIT_OPTIONS = [
    { value: 'ML Millilitre', label: 'ML' },
    { value: 'L Litre', label: 'L' },
] as const;

export const CPM_VOLUME_SETTING_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
} as const;

export const CPM_VOLUME_DEFAULT_UNIT = 'ML Millilitre';

export function volumeUnitShortLabel(unit?: string | null): string {
    if (!unit) return '';
    const mapped = CPM_VOLUME_UNIT_SHORT[unit];
    if (mapped) return mapped;
    const first = unit.trim().split(/\s+/)[0];
    return first ? first.toUpperCase() : unit;
}

export function formatVolumeLabel(volumeMl?: number | null, volumeUnit?: string | null): string {
    if (volumeMl == null) return '—';
    const short = volumeUnitShortLabel(volumeUnit);
    return short ? `${volumeMl} ${short}` : String(volumeMl);
}

/** Display pack size as `750 ML` / `30,000 ML` — same convention as Master Editor / Volume Settings. */
export function formatCpmPackMl(volumeMl: number, volumeUnit?: string | null): string {
    const short = volumeUnitShortLabel(volumeUnit || CPM_VOLUME_DEFAULT_UNIT);
    return `${Number(volumeMl).toLocaleString('en-IN')} ${short}`;
}

/** Compact ranks: `750 → 700 → 375 ML` (one unit suffix, unicode arrow). */
export function formatCpmCompactRankMl(
    packs: Array<{ volumeMl: number; volumeUnit?: string | null }>
): string {
    if (packs.length === 0) return '';
    const nums = packs.map((p) => Number(p.volumeMl).toLocaleString('en-IN'));
    const unit = volumeUnitShortLabel(packs[0]?.volumeUnit || CPM_VOLUME_DEFAULT_UNIT);
    return `${nums.join(' \u2192 ')} ${unit}`;
}

/** Standard-pack header chain: `750 ML → 700 ML → 375 ML`. */
export function formatCpmRankChainMl(
    packs: Array<{ volumeMl: number; volumeUnit?: string | null }>
): string {
    return packs.map((p) => formatCpmPackMl(p.volumeMl, p.volumeUnit)).join(' \u2192 ');
}

const CPM_EXCISE_PACK_KEY_SEP = '\u0001';

export function cpmExcisePackKey(volumeMl: number, volumeUnit: string): string {
    return `${Number(volumeMl)}${CPM_EXCISE_PACK_KEY_SEP}${volumeUnit}`;
}

export function parseCpmExcisePackKey(
    key: string
): { volumeMl: number; volumeUnit: string } | null {
    const i = key.indexOf(CPM_EXCISE_PACK_KEY_SEP);
    if (i <= 0) return null;
    const volumeMl = Number(key.slice(0, i));
    if (!Number.isFinite(volumeMl) || volumeMl <= 0) return null;
    return { volumeMl, volumeUnit: key.slice(i + CPM_EXCISE_PACK_KEY_SEP.length) };
}

/** Resolve Master Editor area → API `level` for the top-level table. */
export function masterListLevel(
    area: 'brands' | 'catalog' | 'stateProducts',
    catalogView: 'combined' | 'products' | 'variants'
): 'brand' | 'product' | 'variant' | 'stateProduct' {
    if (area === 'brands') return 'brand';
    if (area === 'stateProducts') return 'stateProduct';
    return catalogView === 'variants' ? 'variant' : 'product';
}

export const CPM_BAND_OPTIONS = [
    { value: CPM_CONFIDENCE_BAND.VERY_HIGH, label: 'Very high' },
    { value: CPM_CONFIDENCE_BAND.HIGH, label: 'High' },
    { value: CPM_CONFIDENCE_BAND.MODERATE, label: 'Moderate' },
    { value: CPM_CONFIDENCE_BAND.LOW, label: 'Low' },
] as const;

export const CPM_METHOD_OPTIONS = [
    { value: CPM_LINK_METHOD.BARCODE, label: 'Barcode' },
    { value: CPM_LINK_METHOD.DETERMINISTIC, label: 'Deterministic' },
    { value: CPM_LINK_METHOD.FUZZY, label: 'Fuzzy' },
    { value: CPM_LINK_METHOD.LLM, label: 'LLM' },
    { value: CPM_LINK_METHOD.HUMAN, label: 'Human' },
] as const;

export const CPM_RECORD_TYPE_OPTIONS = [
    { value: CPM_RECORD_TYPE.BRAND, label: 'Brand' },
    { value: CPM_RECORD_TYPE.PRODUCT, label: 'Product' },
    { value: CPM_RECORD_TYPE.VARIANT, label: 'Variant' },
] as const;

export const CPM_SIGNAL_OPTIONS = [
    { value: CPM_QUEUE_SIGNAL.DUP, label: 'Possible duplicate' },
    { value: CPM_QUEUE_SIGNAL.NONE, label: 'No flags' },
] as const;

/** Composite sort keys: `${sortBy}:${sortDir}` — empty string = default. */
export const CPM_PENDING_SORT_OPTIONS = [
    { value: '', label: 'Newest first (default)', short: 'Newest first' },
    { value: 'name:asc', label: 'Record name — A to Z', short: 'Name A→Z' },
    { value: 'name:desc', label: 'Record name — Z to A', short: 'Name Z→A' },
    { value: 'brand:asc', label: 'Brand — A to Z', short: 'Brand A→Z' },
    { value: 'brand:desc', label: 'Brand — Z to A', short: 'Brand Z→A' },
    {
        value: 'createdAt:asc',
        label: 'Waiting — longest first',
        short: 'Waiting — longest',
    },
    {
        value: 'createdAt:desc',
        label: 'Waiting — most recent',
        short: 'Waiting — newest',
    },
] as const;

export const CPM_LINK_SORT_OPTIONS = [
    { value: '', label: 'Highest confidence (default)', short: 'Highest confidence' },
    { value: 'name:asc', label: 'Record name — A to Z', short: 'Name A→Z' },
    { value: 'name:desc', label: 'Record name — Z to A', short: 'Name Z→A' },
    { value: 'createdAt:asc', label: 'Waiting — longest first', short: 'Waiting — longest' },
    { value: 'createdAt:desc', label: 'Waiting — most recent', short: 'Waiting — newest' },
] as const;

export const CPM_BARCODE_SORT_OPTIONS = [
    { value: '', label: 'Newest conflict first (default)', short: 'Newest first' },
    { value: 'barcode:asc', label: 'Barcode — low to high', short: 'Barcode ↑' },
    { value: 'barcode:desc', label: 'Barcode — high to low', short: 'Barcode ↓' },
    {
        value: 'claimCount:desc',
        label: 'Claiming products — most first',
        short: 'Claimants — most',
    },
    {
        value: 'claimCount:asc',
        label: 'Claiming products — fewest first',
        short: 'Claimants — fewest',
    },
    {
        value: 'reportCount:desc',
        label: 'Reporting store brands — most first',
        short: 'Reporters — most',
    },
    {
        value: 'reportCount:asc',
        label: 'Reporting store brands — fewest first',
        short: 'Reporters — fewest',
    },
    {
        value: 'firstConflictAt:asc',
        label: 'First conflict — oldest first',
        short: 'Oldest first',
    },
    {
        value: 'firstConflictAt:desc',
        label: 'First conflict — newest first',
        short: 'Newest first',
    },
] as const;

/** Attention strip alert types from GET /product-master/stats */
export const CPM_ATTENTION_TYPE = {
    BATCH_JOB_FAILED: 'BATCH_JOB_FAILED',
    BATCH_JOB_INFRA_LEFTOVER: 'BATCH_JOB_INFRA_LEFTOVER',
    BATCH_JOB_STALE: 'BATCH_JOB_STALE',
    STATE_NOT_CURATED: 'STATE_NOT_CURATED',
    BARCODE_CONFLICTS: 'BARCODE_CONFLICTS',
    REVIEW_BACKLOG: 'REVIEW_BACKLOG',
} as const;

export const CPM_ATTENTION_LINK_SCREEN = {
    OVERVIEW: 'OVERVIEW',
    EXCISE_CATEGORY_CURATION: 'EXCISE_CATEGORY_CURATION',
    VERIFICATION_QUEUE: 'VERIFICATION_QUEUE',
} as const;

/** Catalog-health stacked bar segment colors (design). */
export const CPM_STATUS_BAR_COLORS = {
    PENDING: '#C7C3FA',
    ACTIVE: 'var(--cpm-success-dot)',
    NEEDS_REVIEW: 'var(--cpm-warning-dot)',
    MERGED: 'var(--cpm-text-tertiary)',
    REJECTED: 'var(--cpm-text-tertiary)',
} as const;

export function formatCpmCount(n: number): string {
    return n.toLocaleString('en-IN');
}

/** Excise category / subcategory name max lengths (backend EXCISE_CATEGORY limits). */
export const CPM_EXCISE_CATEGORY_MAX_LENGTH = 100;
export const CPM_EXCISE_SUBCATEGORY_MAX_LENGTH = 80;

/** Per-state curated license types (backend STATE_LICENSE_TYPE limits). */
export const CPM_LICENSE_TYPE_CODE_MAX_LENGTH = 40;
export const CPM_LICENSE_TYPE_LABEL_MAX_LENGTH = 80;
export const CPM_LICENSE_TYPE_MAX_COUNT = 50;

export const CPM_LICENSE_TYPES_COPY = {
    TITLE: 'License types',
    EMPTY: 'No license types yet. Add the codes merchants will pick for stores in this state.',
    ADD: 'Add license type',
    SAVE: 'Save license types',
    SAVING: 'Saving…',
    UNSAVED: 'Unsaved changes',
    HINT_BLANK: 'Add a code to save the new row',
    FOOTER_HELP: 'Click a code to edit or remove it. One save for the whole list.',
    CODE_PLACEHOLDER: 'FL-2',
    LABEL_PLACEHOLDER: 'Foreign Liquor — retail',
    CODE_FIELD: 'Code · stored and matched',
    LABEL_FIELD: 'Label · what merchants read',
    CHIP_NEW_CODE: 'new code',
    CHIP_ADD_LABEL: 'add a label',
    CHIP_LABEL_EQ_CODE: 'label = code',
    EDIT_TITLE: 'Edit this license type',
    DONE: 'Done',
    REMOVE: 'Remove',
    DISMISS: 'Dismiss',
    WHY_TITLE: 'Why two fields',
    WHY_BODY:
        'The code is stored on the store and matched exactly. The label is only what merchants read in the dropdown, so renaming it is always safe.',
    WHY_FOOTNOTE: 'Master Editor offers this same list per state.',
    ISSUE_MAX: 'A state cannot have more than 50 license types',
    MASTER_EMPTY_HELPER: 'Curate license types for this state first.',
} as const;

export function cpmLicenseTypesHeaderNote(stateName: string): string {
    return `Merchants pick one of these when a store in ${stateName} is set up.`;
}

export function cpmLicenseTypesSavedToast(stateName: string, count: number): string {
    return count
        ? `License types saved for ${stateName}.`
        : `License types cleared for ${stateName}.`;
}

export function cpmLicenseTypeDuplicateIssue(code: string): string {
    return `Two rows use the code ${code}. Each code can appear once in a state.`;
}

export function cpmLicenseTypeEmptyCodeIssue(label: string): string {
    return `Add a code for \u201C${label}\u201D \u2014 the label on its own is not stored.`;
}

/** Dropdown options for a state's curated license types. Orphan selected codes stay listed so they can be cleared. */
export function cpmLicenseTypeOptions(
    types: readonly { code: string; label: string }[] | undefined,
    selected: readonly string[] = []
): { value: string; label: string }[] {
    const options: { value: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const t of types ?? []) {
        if (!t.code || seen.has(t.code)) continue;
        seen.add(t.code);
        options.push({ value: t.code, label: t.label?.trim() ? t.label : t.code });
    }
    for (const code of selected) {
        if (!code || seen.has(code)) continue;
        seen.add(code);
        options.push({ value: code, label: code });
    }
    return options;
}

export const CPM_EXCISE_CATEGORY_LIST_LIMIT = 200;

/** Copy for license-implied excise assignment — match the CPM prototype 1:1. */
export const CPM_LICENCE_EXCISE_COPY = {
    ON_STORE_LICENCE: 'On this store\u2019s licence',
    LICENSED_FIRST: 'The store\u2019s licensed categories are listed first.',
    NO_LICENCES: 'This store has no licences on file, so nothing is highlighted.',
    THIS_ONE_DOES_NOT: 'This one does not.',
    PICK_TO_CREATE: 'Pick an excise category to create the state product.',
    SELECT_PLACEHOLDER: 'Select an excise category',
    BULK_NOTE: 'New state products without a category land in Master Editor as Needs review.',
    ASSIGN_LABEL: 'Assign category',
    ASSIGN_HELP: 'Pick the excise category for this state. This cannot be changed later from here.',
    LOCKED_HELP: 'This cannot be changed later from here.',
    APPROVE_BLOCKED_LINK: 'Open the record and assign it',
    TAG_REQUIRED: 'REQUIRED',
    TAG_OPTIONAL: 'OPTIONAL',
    TAG_SET_ONCE: 'Set once',
    TAG_REQUIRED_FIELD: 'Required',
} as const;

export function cpmLicenceExciseNeededHelper(stateName: string): string {
    return `Needed only if this variant has no state product in ${stateName} yet.`;
}

export function cpmLicenceExciseCreateHelper(stateName: string): string {
    return `Active excise categories in ${stateName}.`;
}

export function cpmLicenceExciseInheritText(stateName: string): string {
    return `This variant already has a state product in ${stateName} \u2014 its category carries over, and anything picked here is ignored.`;
}

export function cpmLicenceExcisePickForState(stateName: string): string {
    return `Pick a category for ${stateName}`;
}

export function cpmLicenceExciseApproveBlocked(stateName: string): string {
    return `Assign an excise category before approving this state product. Open the record and pick the category for ${stateName} \u2014 it can only be set once.`;
}

export function isActiveExciseCategory(cat: { status?: string | null }): boolean {
    return (cat.status || 'ACTIVE') === 'ACTIVE';
}

/** Licensed ids first (A–Z within the group), then the rest A–Z. */
export function sortExciseCategoriesForStoreLicence<T extends { _id: string; category: string }>(
    categories: readonly T[],
    licensedIds: readonly string[]
): Array<T & { licensed: boolean }> {
    const licensedSet = new Set(licensedIds.map(String));
    const byName = (a: T, b: T) =>
        String(a.category).localeCompare(String(b.category), undefined, { sensitivity: 'base' });
    const licensed = categories.filter((c) => licensedSet.has(String(c._id))).sort(byName);
    const rest = categories.filter((c) => !licensedSet.has(String(c._id))).sort(byName);
    return [
        ...licensed.map((c) => ({ ...c, licensed: true })),
        ...rest.map((c) => ({ ...c, licensed: false })),
    ];
}

export const CPM_EXCISE_MAX_STANDARD_RANKS = 3;

export const CPM_EXCISE_RANK_LABELS = ['1st', '2nd', '3rd'] as const;

export const CPM_EXCISE_PACK_COPY = {
    REFRESH_IDLE: 'Refresh sizes',
    REFRESH_IN_FLIGHT: 'Refreshing sizes',
    REFRESH_RETRY: 'Retry refresh',
    STANDARD_PACK: 'Standard pack',
    SEEN_SIZES: 'Seen sizes',
    NOT_SET: 'Not set',
    NO_STANDARD_PACK: 'No standard pack',
    NO_SIZES_SEEN: 'No sizes seen',
    RE_PICK_SUFFIX: ' · re-pick',
    STALE_OPTION_SUFFIX: ' — no longer seen',
    RANK_SAVING: 'Saving',
    RANK_RETRY: 'Retry',
    UNSET: 'unset',
    RANK_HINT_1:
        'The bottle we use to judge retail floor and ceiling for products in this category.',
    RANK_HINT_2: 'Used when a product has no 1st size.',
    RANK_HINT_3: 'Used when a product has neither.',
    BLOCKED_RANKS:
        'No pack sizes seen in this category yet, so there is nothing to rank. Refresh sizes once its products exist — leaving the ranks empty is fine.',
    STALE_SAVE_HINT: 'Pick another size. Saving any rank will drop sizes that are no longer seen.',
    OPTIONAL_NONE_SET: 'Optional — none set',
    STALE_SUMMARY: 'One pick is no longer in the seen list',
    WHY_RANKS_TITLE: 'Why ranks matter',
    WHY_RANKS_BODY:
        'The merchant\'s retail floor and ceiling are checked against the standard bottle only. If that price is in range, every size of the product is kept.',
    WHY_RANKS_EXAMPLE_SIZE: '750 ML',
    WHY_RANKS_EXAMPLE: 'in range keeps 180 ml too',
} as const;

export const CPM_EXCISE_RANK_HINTS = [
    CPM_EXCISE_PACK_COPY.RANK_HINT_1,
    CPM_EXCISE_PACK_COPY.RANK_HINT_2,
    CPM_EXCISE_PACK_COPY.RANK_HINT_3,
] as const;

export function cpmExciseSeenHeading(count: number): string {
    return count > 0
        ? `${CPM_EXCISE_PACK_COPY.SEEN_SIZES} · ${formatCpmCount(count)}`
        : CPM_EXCISE_PACK_COPY.SEEN_SIZES;
}

export function cpmExciseSeenEmptyCopy(stateName: string, category: string): string {
    return `No ${stateName} products in ${category} yet, so no pack sizes have been seen. You can leave the ranks empty.`;
}

export function cpmExciseSeenNote(stateName: string, category: string): string {
    return `Pack sizes seen on ${stateName} ${category} products only.`;
}

export function cpmExciseReadingStamp(stateName: string): string {
    return `Reading ${stateName} products in this category…`;
}

export function cpmExciseStaleOptionLabel(volumeMl: number, volumeUnit?: string | null): string {
    return `${formatCpmPackMl(volumeMl, volumeUnit)}${CPM_EXCISE_PACK_COPY.STALE_OPTION_SUFFIX}`;
}

export function cpmExciseStalePillTitle(volumeMl: number, volumeUnit?: string | null): string {
    return `${formatCpmPackMl(volumeMl, volumeUnit)} is no longer in this category's seen sizes — pick again`;
}

export function cpmExciseRefreshSuccessCopy(name: string, n: number): string {
    return `Seen sizes updated for ${name} — ${formatCpmCount(n)} pack sizes. Ranks unchanged.`;
}

export function cpmExciseRefreshEmptyCopy(name: string): string {
    return `No state products in ${name} yet, so there are no pack sizes to show. Ranks can stay empty.`;
}

export function cpmExciseRefreshFailCopy(name: string): string {
    return `Couldn't refresh sizes for ${name}. The list below is the last one we saved — nothing was changed.`;
}

export function cpmExciseRankSavedCopy(name: string, rankText: string): string {
    return `Standard pack for ${name} is now ${rankText}.`;
}

export function cpmExciseRankClearedCopy(name: string): string {
    return `Standard pack cleared for ${name}. Leaving ranks empty is fine.`;
}

export function cpmExciseRankFailCopy(
    name: string,
    rankText: string | null,
    apiMessage?: string | null
): string {
    const fromApi = typeof apiMessage === 'string' ? apiMessage.trim() : '';
    if (fromApi) return fromApi;
    const still = rankText && rankText.length > 0 ? rankText : CPM_EXCISE_PACK_COPY.UNSET;
    return `Couldn't save the standard pack for ${name}. It is still ${still} — try again.`;
}

export function cpmExciseStaleAfterRefreshCopy(goneLabel: string, name: string): string {
    return `${goneLabel} is no longer in the seen list for ${name}. The rank was left as-is — pick another size.`;
}

export const CPM_STATE_FILTER_TAB = {
    all: 'all',
    live: 'live',
    pending: 'pending',
    off: 'off',
} as const;

export const CPM_STATE_FILTER_TABS = [
    { key: CPM_STATE_FILTER_TAB.all, label: 'All' },
    { key: CPM_STATE_FILTER_TAB.live, label: 'Live' },
    { key: CPM_STATE_FILTER_TAB.pending, label: 'Needs setup' },
    { key: CPM_STATE_FILTER_TAB.off, label: 'Not live' },
] as const;

/** Active / trial store counts for Excise list + detail (no inactive suffix). */
export function formatStoreCounts(active: number, trial: number): string {
    if (!active && !trial) return 'No stores yet';
    const parts: string[] = [];
    if (active) parts.push(`${formatCpmCount(active)} active`);
    if (trial) parts.push(`${formatCpmCount(trial)} trial`);
    const total = active + trial;
    return `${parts.join(' · ')} ${total === 1 ? 'store' : 'stores'}`;
}

export function formatCpmPercent(linked: number, total: number): string {
    if (total <= 0) return '0.0%';
    return `${((linked / total) * 100).toFixed(1)}%`;
}

/** Resolve Overview attention `link` object → in-app path or hash. */
export function attentionHref(link: Record<string, string>): string {
    const screen = link.screen;
    if (screen === CPM_ATTENTION_LINK_SCREEN.OVERVIEW) {
        return `#${link.anchor ?? 'batch-jobs'}`;
    }
    if (screen === CPM_ATTENTION_LINK_SCREEN.EXCISE_CATEGORY_CURATION) {
        const state = link.state ? `?state=${encodeURIComponent(link.state)}` : '';
        return `/central-product-master/excise-category${state}`;
    }
    if (screen === CPM_ATTENTION_LINK_SCREEN.VERIFICATION_QUEUE) {
        if (link.type === CPM_QUEUE_TYPE.BARCODE_CONFLICT) {
            return '/central-product-master/verification-queue?tab=barcode';
        }
        return '/central-product-master/verification-queue?tab=link';
    }
    return '/central-product-master';
}

export function attentionActionLabel(type: string): string {
    if (
        type === CPM_ATTENTION_TYPE.BATCH_JOB_FAILED ||
        type === CPM_ATTENTION_TYPE.BATCH_JOB_INFRA_LEFTOVER ||
        type === CPM_ATTENTION_TYPE.BATCH_JOB_STALE
    ) {
        return 'Open job health →';
    }
    if (type === CPM_ATTENTION_TYPE.STATE_NOT_CURATED) {
        return 'Open excise category readiness →';
    }
    if (type === CPM_ATTENTION_TYPE.BARCODE_CONFLICTS) {
        return 'Resolve conflicts →';
    }
    if (type === CPM_ATTENTION_TYPE.REVIEW_BACKLOG) {
        return 'Open verification queue →';
    }
    return 'Open →';
}

/** Raw provenance createdBy (e.g. SEED_IMPORT, SYSTEM, HUMAN). */
export function formatCreatedVia(createdBy?: string | null): string {
    return createdBy || '—';
}

export function formatFlaggedDate(iso?: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function shortVolumeUnit(unit?: string | null): string {
    if (!unit) return '';
    const u = unit.toUpperCase();
    if (u.includes('MILLILITRE') || u === 'ML' || u.startsWith('ML ')) return 'ML';
    if (u.includes('LITRE') || u === 'L' || u.startsWith('L ')) return 'L';
    return unit;
}

/** Short ObjectId for list copy when a duplicate name is missing. */
export function shortMasterId(id?: string | null): string {
    if (!id) return '—';
    return id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/** Display name for a possible-duplicate target; falls back to a short id. */
export function duplicateTargetName(item: {
    possibleDuplicate?: { name?: string | null; masterId?: string } | null;
    possibleDuplicateOf?: string | null;
}): string {
    const name = item.possibleDuplicate?.name?.trim();
    if (name) return name;
    return shortMasterId(item.possibleDuplicate?.masterId || item.possibleDuplicateOf);
}

export function packLabel(item: {
    volumeMl?: number;
    volumeUnit?: string;
    containerType?: string;
}): string | null {
    const parts: string[] = [];
    if (item.volumeMl != null) {
        const unit = shortVolumeUnit(item.volumeUnit);
        parts.push(unit ? `${item.volumeMl} ${unit}` : String(item.volumeMl));
    }
    if (item.containerType) parts.push(item.containerType);
    return parts.length ? parts.join(' · ') : null;
}

/** Map ProductLink sourceType (STORE_BRAND_*) or bare BRAND/PRODUCT/VARIANT → chip record type. */
export function sourceTypeToRecordType(
    sourceType: string
): (typeof CPM_RECORD_TYPE)[keyof typeof CPM_RECORD_TYPE] | string {
    const t = String(sourceType || '').toUpperCase();
    if (t === 'STORE_BRAND_BRAND' || t === 'BRAND') return CPM_RECORD_TYPE.BRAND;
    if (t === 'STORE_BRAND_PRODUCT' || t === 'PRODUCT') return CPM_RECORD_TYPE.PRODUCT;
    if (t === 'STORE_BRAND_VARIANT' || t === 'VARIANT') return CPM_RECORD_TYPE.VARIANT;
    return sourceType;
}

export function recordTypeLabel(recordType: string): string {
    const mapped = sourceTypeToRecordType(recordType);
    if (mapped === CPM_RECORD_TYPE.BRAND || mapped === 'brand') return 'Brand';
    if (mapped === CPM_RECORD_TYPE.PRODUCT || mapped === 'product') return 'Product';
    if (mapped === CPM_RECORD_TYPE.VARIANT || mapped === 'variant') return 'Variant';
    return recordType;
}

export function levelFromRecordType(recordType: string): 'brand' | 'product' | 'variant' {
    const mapped = sourceTypeToRecordType(recordType);
    if (mapped === CPM_RECORD_TYPE.BRAND) return 'brand';
    if (mapped === CPM_RECORD_TYPE.PRODUCT) return 'product';
    return 'variant';
}

/** Map ProductLink.masterType → Master Editor openLevel. */
export function openLevelFromMasterType(
    masterType: string | undefined
): 'brand' | 'product' | 'variant' {
    if (masterType === CPM_PRODUCT_LINK_MASTER_TYPE.ALCOHOL_BRAND) return 'brand';
    if (masterType === CPM_PRODUCT_LINK_MASTER_TYPE.ALCOHOL_PRODUCT) return 'product';
    return 'variant';
}

/** Deep-link into Master Editor for a Link Review candidate. */
export function masterEditorRecordHref(masterType: string | undefined, masterId: string): string {
    const openLevel = openLevelFromMasterType(masterType);
    const params = new URLSearchParams();
    if (openLevel === 'brand') {
        params.set('level', 'brands');
    } else if (openLevel === 'product') {
        params.set('level', 'catalog');
        params.set('view', 'products');
    } else {
        params.set('level', 'catalog');
        params.set('view', 'variants');
    }
    params.set('openId', masterId);
    params.set('openLevel', openLevel);
    return `/central-product-master/master-editor?${params.toString()}`;
}

export function linkReviewModelCopy(params: {
    method?: string | null;
    model?: string | null;
    reviewedByName?: string | null;
}): { line: string; explain: string } {
    const method = String(params.method ?? '').toUpperCase();
    const model = String(params.model ?? '').toUpperCase();
    const reviewer = params.reviewedByName?.trim() || '';

    if (method === CPM_LINK_METHOD.HUMAN) {
        return {
            line: reviewer ? `Confirmed by ${reviewer}` : 'Confirmed by a reviewer',
            explain: 'A reviewer manually linked this tenant record to a master.',
        };
    }
    if (method === CPM_LINK_METHOD.LLM) {
        const modelLabel = model === 'DEEPSEEK' || model === '' ? 'DeepSeek' : model;
        return {
            line: `${modelLabel} reviewed this match`,
            explain:
                'An AI model compared the tenant record against every candidate and picked this one.',
        };
    }
    if (method === CPM_LINK_METHOD.BARCODE) {
        return {
            line: 'Automated scoring picked this match',
            explain: 'No AI involved — matched by barcode registry identity.',
        };
    }
    if (method === CPM_LINK_METHOD.DETERMINISTIC) {
        return {
            line: 'Automated scoring picked this match',
            explain: 'No AI involved — matched by exact identifiers.',
        };
    }
    if (method === CPM_LINK_METHOD.FUZZY) {
        return {
            line: 'Automated scoring picked this match',
            explain: 'No AI involved — matched by weighted text-and-attribute scoring.',
        };
    }
    return {
        line: 'Matching pipeline made this call',
        explain: 'The linking pipeline scored candidates and left this row for review.',
    };
}
